import { useEffect } from "react";
import { usePatchPortfolioBuildingSavingsMutation } from "web_myplann/Api/Portfolio/Mutations/usePatchPortfolioBuildingSavingsMutation";
import type { PortfolioDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getPortfolioTitle } from "web_myplann/Portfolio/Components/PortfolioSections/portfolioListItemHelpers";
import { getBuildingSavingsEditFormSchema } from "web_myplann/Portfolio/Forms/BuildingSavingsForm/buildingSavingsFormSchema";
import { mapBuildingSavingsEditFormToRequest } from "web_myplann/Portfolio/Forms/BuildingSavingsForm/mapBuildingSavingsFormToRequest";
import { mapPortfolioToBuildingSavingsForm } from "web_myplann/Portfolio/Forms/BuildingSavingsForm/mapPortfolioToBuildingSavingsForm";
import { useEditPortfolioForm } from "web_myplann/Portfolio/PortfolioShared/useEditPortfolioForm";

type UseEditBuildingSavingsFormArgs = {
	portfolioId: number;
	portfolio: PortfolioDetailResponseDTO;
	detailRoute: string;
};

export const useEditBuildingSavingsForm = ({
	portfolioId,
	portfolio,
	detailRoute,
}: UseEditBuildingSavingsFormArgs) => {
	const { t } = useResource();
	const { mutate, isPending } =
		usePatchPortfolioBuildingSavingsMutation(portfolioId);

	const { form, onSubmit, leaveConfirmation } = useEditPortfolioForm({
		schema: getBuildingSavingsEditFormSchema(t, {
			lockKeyFields: !portfolio.allowKeyPropertiesEditByClient,
		}),
		defaultValues: mapPortfolioToBuildingSavingsForm(portfolio),
		mutate: (data, options) =>
			mutate(
				mapBuildingSavingsEditFormToRequest(data, {
					payment: form.getValues("payment") ?? null,
					paymentFrequency: form.getValues("paymentFrequency"),
				}),
				options,
			),
		successToast: t(Resources.Portfolio.EditPortfolio.savedToast, {
			type: getPortfolioTitle(portfolio, t),
		}),
		redirectTo: detailRoute,
		isPending,
	});

	useEffect(() => {
		form.trigger();
	}, [form.trigger]);

	return { form, onSubmit, isPending, leaveConfirmation };
};
